import "./PrintMenu.css";
import React, { useState } from "react";
import Button from "@mui/material/Button";
import Menu from "@mui/material/Menu";
import MenuItem from "@mui/material/MenuItem";
import DownloadForOfflineIcon from "@mui/icons-material/DownloadForOffline";
import { PDFDocument } from "pdf-lib";

export default function PrintMenu({
  pdfViewerArray,
  printAllPDFViewerArray,
  selectedTab,
  selectedStyle,
  selectedProjectName,
  selectedModel,
  selectedOrientation,
  setIsLoading,
  setError,
}) {
  const [expandMenu, setExpandMenu] = useState(null);
  const open = Boolean(expandMenu);

  const buildPdfFileName = ({ downloadType }) => {
    const model = selectedTab === "SALES" ? selectedModel.name : selectedModel.code;
    const discipline =
      selectedTab === "SALES" ? "Blackline" :
      selectedTab === "COORDINATION" ? "Coordination" : "IFC";
    const orientation = selectedOrientation.toUpperCase();
    const date = new Date().toISOString().slice(0, 10);

    const baseName = `${selectedProjectName} - ${model} - ${selectedStyle} - ${orientation} - ${discipline} - ${date}`;

    return downloadType === "download-selected"
      ? `${baseName}.pdf`
      : `${baseName} - All.pdf`;
  };

  const handleExpandMenuClick = (event) => {
    setExpandMenu(event.currentTarget);
  };

  const handleExpandMenuClose = () => {
    setExpandMenu(null);
  };

  const handleDownloadSelectedClick = () => {
    mergePDFtoPrint(pdfViewerArray, "download-selected");
  };

  const handleDownloadAllClick = () => {
    mergePDFtoPrint(printAllPDFViewerArray, "download-all");
  };

  const mergePDFtoPrint = async (
    ViewerInputArray,
    download_type
  ) => {
    if (!selectedModel || !selectedOrientation || !ViewerInputArray?.length) {
      return;
    }

    // Indicate that the PDF merging process is starting
    setIsLoading((prevState) => ({
      ...prevState,
      mergePDF: true,
    }));

    try {
      // Create a new PDF document to hold the merged content
      const mergedPdf = await PDFDocument.create();

      // Loop through each PDF path (URL)
      for (const singlePDFPath of ViewerInputArray) {
        const response = await fetch(singlePDFPath); // Fetch the PDF data
        const pdfBytes = await response.arrayBuffer(); // Convert to bytes
        const pdfDoc = await PDFDocument.load(pdfBytes); // Load the PDF

        // Copy all pages from the loaded PDF and add them to the merged PDF
        const copiedPages = await mergedPdf.copyPages(
          pdfDoc,
          pdfDoc.getPageIndices()
        );
        copiedPages.forEach((page) => mergedPdf.addPage(page));
      }

      // Save the merged PDF as a byte array
      const mergedPdfFile = await mergedPdf.save();

      // Create a Blob from the byte array and generate a download link
      const blob = new Blob([mergedPdfFile], { type: "application/pdf" });
      const link = document.createElement("a");
      link.href = URL.createObjectURL(blob);
      link.download = buildPdfFileName({ downloadType: download_type})
      link.click(); // Trigger the download

      setError((prevState) => ({
        ...prevState,
        mergePDF: "",
      }));
    } catch (err) {
      setError((prevState) => ({
        ...prevState,
        mergePDF: err.message,
      }));
    } finally {
      // Set the merging state to false after completion (whether successful or not)
      setIsLoading((prevState) => ({
        ...prevState,
        mergePDF: false,
      }));
    }
  };

  return (
    <div className="PrintMenu__container">
      <Button
        id="basic-button"
        aria-controls={open ? "basic-menu" : undefined}
        aria-haspopup="true"
        aria-expanded={open ? "true" : undefined}
        onClick={handleExpandMenuClick}
        startIcon={
          <DownloadForOfflineIcon
            sx={{
              width: "35px",
              height: "35px",
              color: "title.main",
              "&:hover": { color: "primary.btn_hover" },
            }}
          />
        }
        disableRipple
        disabled={!selectedStyle}
        sx={{
          padding: "0",
          transition: "none",
          "&:hover": {
            backgroundColor: "transparent",
          },
          "&:active": {
            transition: "none",
            backgroundColor: "transparent", // Remove background color on active (click)
          },
        }}
      ></Button>

      <Menu
        id="basic-menu"
        anchorEl={expandMenu}
        open={open}
        onClose={handleExpandMenuClose}
        slotProps={{
          list: {
            "aria-labelledby": "basic-button",
          },
        }}
        sx={{
          transition: "none", // Disable transition on the menu
        }}
      >
        <MenuItem
          onClick={handleDownloadSelectedClick}
          disabled={!selectedStyle || pdfViewerArray.length < 1}
        >
          Download Selected
        </MenuItem>
        <MenuItem
          onClick={handleDownloadAllClick}
          disabled={!selectedStyle || printAllPDFViewerArray.length < 1}
        >
          Download All
        </MenuItem>
      </Menu>
    </div>
  );
}
